import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAssetData } from '../../api/profile'
import { useAuth } from '../../auth/authContext'
import { readCachedAssetData, writeCachedAssetData } from './profileCache'

/**
 * Server-state hook for the signed-in user's profile / asset data.
 *
 * The last successful response is mirrored to localStorage under that user's
 * asset id and used as `initialData` on the next load, so the header can render
 * the real name immediately on refresh instead of flashing the token-claim
 * fallback. We pass `initialDataUpdatedAt: 0` so the cached value is treated as
 * stale and a fresh fetch still runs in the background. A different user's
 * mirror is never read.
 */
export function useProfile() {
  const { authenticated, claims } = useAuth()
  const assetId = claims?.assetId || undefined
  const cached = readCachedAssetData(assetId)
  const query = useQuery({
    queryKey: ['profile', 'asset-data', assetId ?? null],
    queryFn: getAssetData,
    enabled: authenticated, // don't fire until Keycloak confirms we're logged in
    initialData: cached,
    initialDataUpdatedAt: cached ? 0 : undefined,
    // Live status source: refetch on every mount rather than serving the 30s
    // staleTime cache. Returning to /home (e.g. after submitting the Application
    // of Interest form on its own route, which unmounts HomeScreen) must
    // re-resolve the caller's status — prospect → applicant — instead of showing
    // the stale composition until the cache ages out. The localStorage mirror
    // still paints the last-known name/status instantly via initialData.
    refetchOnMount: 'always',
  })

  useEffect(() => {
    if (!query.isSuccess || !assetId) return
    try {
      writeCachedAssetData(assetId, query.data)
    } catch {
      // storage full / unavailable — cache is best-effort, ignore
    }
  }, [query.isSuccess, query.data, assetId])

  return query
}
